import { HeartIcon } from 'lucide-react-native';
import { Pressable, type PressableStateCallbackType } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { useToggleCommentLike } from '../../hooks/useToggleCommentLike';
import type { AnyComment } from '../../model/types';

import { Button, ButtonText } from '@/components/ui/button';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';

const LikeButton = ({
  onLike,
  liked,
  likes,
  textClassName = 'text-typography-600',
}: {
  onLike: () => void;
  liked: boolean;
  likes: number;
  size?: number;
  textClassName?: string;
}) => {
  const { colors } = useColors();

  return (
    <Pressable
      onPress={onLike}
      accessibilityRole="button"
      accessibilityLabel={`${liked ? 'Unlike' : 'Like'} comment, ${likes || 0} ${likes === 1 ? 'like' : 'likes'}`}
      className="min-h-10 min-w-10 justify-center">
      {({ pressed }: PressableStateCallbackType) => (
        <HStack className="ml-3 items-center">
          <HeartIcon
            opacity={pressed ? 0.5 : 1}
            color={colors.primary[600]}
            size={16}
            fill={liked ? colors.primary[600] : colors.transparent}
          />
          <Text className={twJoin('ml-2 font-medium text-sm', textClassName)}>{likes || 0}</Text>
        </HStack>
      )}
    </Pressable>
  );
};

export const CommentFooter = ({
  comment,
  postId,
  onReply,
}: {
  comment: AnyComment;
  postId: number;
  onReply?: (comment: AnyComment) => void;
}) => {
  const { guard } = useAbilityGate();

  const toast = useToaster();

  const likeCommentMutation = useToggleCommentLike({
    postId,
    onFailed: (variables) => {
      toast.showError({
        title: variables.liked ? 'Failed to Unlike Comment' : 'Failed to Like Comment',
        message: "We couldn't process your request. Please try again.",
      });
    },
  });

  const handleLike = () => {
    if (!guard(comment.liked ? 'delete' : 'create', 'CommentLike')) {
      return;
    }

    likeCommentMutation.mutate({ commentId: comment.id, liked: comment.liked });
  };

  const handleReply = () => {
    if (!guard('create', 'Comment')) {
      return;
    }

    onReply?.(comment);
  };

  return (
    <HStack className="mb-6 ml-10 items-center gap-6 web:ml-5">
      <LikeButton onLike={handleLike} liked={comment.liked} likes={comment.likes} size={24} />
      <Button variant="link" size="sm" onPress={handleReply}>
        <ButtonText className="font-medium text-primary-500">Reply</ButtonText>
      </Button>
    </HStack>
  );
};
